import { useAppSelector } from '@/app/redux/hooks';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import { selectUpgradeState } from '@/app/redux/slices/upgradeSlice';
import { BillingInfo } from '@/common/types/organisation';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import {
  formatPrice,
  getCouponValid,
  getPlanBreakdown,
  PLAN_DETAILS,
} from '@/common/utils/license';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { notifyZapier } from '@/common/utils/zapier';
import BillingDetails from '@/dashboard/SettingsView/Billing/BillingDetails';
import PlanDetails from '@/dashboard/Upgrade/PlanDetails';
import api from '@/db/apiClient';
import { CheckIcon, InfoIcon } from '@phosphor-icons/react';
import { Alert } from '@shadcn/components/alert';
import { Button } from '@shadcn/components/button';
import { Separator } from '@shadcn/components/separator';
import { useStripe } from '@stripe/react-stripe-js';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function CheckoutConfirmation() {
  const navigate = useNavigate();
  const stripe = useStripe();

  const {
    selectedPlan,
    seatCount,
    billingCycle,
    paymentMethod,
    clientSecret,
    confirmationTokenId,
    billingInfo: stateBillingInfo,
    currency,
    coupon,
  } = useAppSelector(selectUpgradeState);
  const { organisation } = useOrganisation();
  const userId = useAppSelector(selectUserId);

  const [isLoading, setLoading] = useState(false);
  const [isBillingInfoLoading, setIsBillingInfoLoading] = useState(false);
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [message, setMessage] = useState<string | JSX.Element | null>(null);

  useEffect(() => {
    async function fetchBillingInfo() {
      setIsBillingInfoLoading(true);
      try {
        let billingInfo: BillingInfo | null = null;
        if (paymentMethod === 'card') {
          if (!confirmationTokenId) {
            throw new Error('No confirmation token ID');
          }
          billingInfo = await api.asEducator.summarizeConfirmationTokenDetails({
            confirmationTokenId,
          });
        } else {
          billingInfo = stateBillingInfo;
        }
        if (!billingInfo) {
          throw new Error('No billing info found');
        }
        setBillingInfo(billingInfo);
      } catch (error) {
        console.error('Error fetching billing info:', error);
        toast.error('Failed to load payment details', {
          description: UNKNOWN_ERROR_MESSAGE,
        });
        navigate('/pricing');
      } finally {
        setIsBillingInfoLoading(false);
      }
    }
    fetchBillingInfo();
  }, [paymentMethod, confirmationTokenId, stateBillingInfo, navigate]);

  const handleSubmit = async () => {
    try {
      setLoading(true);

      trackMixpanelEvent('Confirmed Payment', {
        selectedPlan,
        seatCount,
        billingCycle,
        paymentMethod,
        currency,
      });

      if (paymentMethod === 'card') {
        if (!confirmationTokenId || !clientSecret || !stripe) {
          throw new Error(
            'Could not confirm card payment. Please check your payment details and try again',
          );
        }
        const { error } = await stripe.confirmPayment({
          clientSecret,
          confirmParams: {
            confirmation_token: confirmationTokenId,
            return_url: `${window.location.origin}/checkout/return`,
          },
        });
        if (error) {
          throw new Error(error.message);
        }
      } else {
        if (!billingInfo || !seatCount || !organisation) {
          throw new Error(
            'Could not confirm payment. Please check your payment details and try again',
          );
        }
        const quantity = Math.max(
          PLAN_DETAILS[selectedPlan].seatCount,
          seatCount,
        );
        await api.asEducator.confirmInvoicePayment({
          organisationId: organisation.id,
          licenseUpdates: {
            stripeSubscriptionId: null,
            plan: selectedPlan,
            status: 'active',
            billingCycle,
            billingCycleAnchor: Date.now(),
            seatCount: quantity,
            billingInfo,
            currency,
            coupon,
          },
        });
        const { total } = getPlanBreakdown(
          selectedPlan,
          seatCount,
          billingCycle,
          currency,
          coupon?.percentOff ?? null,
          getCouponValid(coupon?.appliedAt, billingCycle),
        );
        await notifyZapier({
          hook: 'subscription',
          event: 'Invoice Payment Confirmed',
          userId: userId ?? 'Unknown',
          organisationId: organisation.id,
          organisationName: organisation.name,
          name: billingInfo.name,
          email: billingInfo.email,
          plan: selectedPlan,
          seatCount: quantity,
          billingCycle,
          currency,
          coupon: coupon?.code ?? 'None',
          total: formatPrice(total, currency, true),
        });
        window.location.href =
          '/checkout/return?redirect_status=succeeded&payment_intent=invoice';
      }
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : UNKNOWN_ERROR_MESSAGE,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex gap-4 h-full">
      <div className="flex flex-col gap-4 w-full">
        {message && <Alert variant="destructive">{message}</Alert>}

        <div className="flex flex-col gap-2 bg-[#fafafa] rounded-md p-4">
          <span className="font-medium text-base md:text-lg flex gap-2 items-end">
            <span className="leading-none">Billing Details</span>
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                navigate('/checkout/payment-details');
              }}
              className="ml-auto h-fit"
            >
              Edit
            </Button>
          </span>
          <Separator className="my-2" />
          <BillingDetails
            billingInfo={billingInfo}
            loading={isBillingInfoLoading}
          />
        </div>
        <PlanDetails
          plan={selectedPlan}
          seatCount={seatCount ?? 1}
          billingCycle={billingCycle}
          currency={currency}
          coupon={coupon}
          onEdit={() => {
            navigate('/pricing');
          }}
        />
        <div className="flex flex-col-reverse md:flex-row items-center justify-between gap-4 md:gap-8">
          <span className="text-xs lg:text-sm text-muted-foreground flex items-center gap-2">
            <InfoIcon className="min-w-4 min-h-4" />
            <span className="inline-block">
              By confirming your subscription, you allow Spellcaster to store
              your billing details and charge you for future payments in
              accordance with our{' '}
              <a
                href="https://spellcaster.education/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block underline cursor-pointer"
              >
                privacy policy
              </a>{' '}
              and{' '}
              <a
                href="https://spellcaster.education/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block underline cursor-pointer"
              >
                terms of service
              </a>
              . You can always cancel your subscription.
            </span>
          </span>
          <Button
            className="gap-2 w-40 self-end md:self-start"
            size="lg"
            onClick={handleSubmit}
            loading={isLoading}
            disabled={!billingInfo}
          >
            Confirm
            <CheckIcon size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}
